import { MaterialReactTable } from "material-react-table";
import React, { useEffect, useRef, useState } from "react";
import { AGENCY_PROPERTY_COLUMNS } from "./propertiesData";
import { MenuItem, TextField, useMediaQuery } from "@mui/material";
import {
  useChangePropertyStatusMutation,
  useDeletePropertyMutation,
} from "../../redux/api/propertiesApi";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { toast } from "react-toastify";
import ShareLocation from "./ShareLocation";
import ShareYoutubeVideo from "./ShareYoutubeVideo";
import { useSearchParams } from "react-router-dom";
import ImageTemplates from "./templates/ImageTemplates";

const DEFAULT_PAGE = 1; // 1-based in URL
const DEFAULT_SIZE = 10; // pageSize in URL (frontend-controlled)

const propertyStatusOptions = [
  { value: 0, label: "Un Publish" },
  { value: 1, label: "Publish" },
  { value: 2, label: "Pending " },
  { value: 3, label: "Draft" },
  { value: 4, label: "Delete" },
  { value: 5, label: "Share PDF" },
  { value: 6, label: "Share Location" },
  { value: 7, label: "Share YouTube Video" },
    { value: 8, label: "Create WhatsApp Story Ad" },

];

export default function PropertiesTable({
  paginationData,
  isFetching,
  data,
  onClickSharePdf,
  isRefetch,
}) {
  const currentAgency = useSelector(selectCurrentagency);
  const [changePropertyStatus] = useChangePropertyStatusMutation();
  const [deleteProperty] = useDeletePropertyMutation();
  const isMobileScreen = useMediaQuery("(max-width:768px)");
const [downloadTemplate, setDownloadTemplate] = useState(false);
  const [shareLocation, setShareLocation] = useState(false);
  const [location, setLocation] = useState([]);
  const [shareYoutubeVideo, setShareYoutubeVideo] = useState(false);
  const [video, setVideo] = useState([]);
const [selectedProperties, setSelectedProperties] = useState([]);
  const tableRef = useRef();

  // --- URL <-> pagination sync (frontend owns pageSize) ---
  const [searchParams, setSearchParams] = useSearchParams();

  const getUrlState = () => {
    const p1 = Number(searchParams.get("page")) || DEFAULT_PAGE; // 1-based
    const size = Number(searchParams.get("size")) || DEFAULT_SIZE;

    return {
      pageIndex: Math.max(0, p1 - 1), // 0-based for MRT
      pageSize: size,
    };
  };

  const [pagination, setPagination] = useState(getUrlState);

  // reflect URL changes (back/forward)
  useEffect(() => {
    const next = getUrlState();
    if (
      pagination.pageIndex !== next.pageIndex ||
      pagination.pageSize !== next.pageSize
    ) {
      setPagination(next);
    }
  }, [searchParams]);

  // write table pagination to URL
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        const page1 = pagination.pageIndex + 1; // back to 1-based
        if (page1 !== DEFAULT_PAGE) next.set("page", String(page1));
        else next.delete("page");

        if (pagination.pageSize !== DEFAULT_SIZE)
          next.set("size", String(pagination.pageSize));
        else next.delete("size");

        return next;
      },
      { replace: true }
    );
  }, [pagination, setSearchParams]);

  // align pageIndex with server PageNumber AFTER fetch (do NOT touch pageSize)
  useEffect(() => {
    if (!paginationData?.PageNumber) return;
    const serverPageIndex = Math.max(0, paginationData.PageNumber - 1);
    setPagination((prev) =>
      prev.pageIndex !== serverPageIndex
        ? { ...prev, pageIndex: serverPageIndex }
        : prev
    );
  }, [paginationData?.PageNumber]);
  // --- end sync ---

const handleStatusChange = async (
  value,
  propertyIds,
  locations,
  selectedData,
  newVideos
) => {
  if (propertyIds.length === 0)
    return toast.warn("Please select at least one property");

  if (value === 4) {
    if (window.confirm("Are you sure you want to delete?")) {
      propertyIds.forEach((id) =>
        deleteProperty({ agencyId: currentAgency.agencyid, propertyId: id })
      );
    }
    return tableRef?.current?.resetRowSelection();
  }

  if (value === 5) return onClickSharePdf(propertyIds);

  if (value === 6) {
    setShareLocation(true);
    setLocation(locations);
    return;
  }

  if (value === 7) {
    setVideo(newVideos);
    return setShareYoutubeVideo(true);
  }

  // ✅ Open ImageTemplates modal
  if (value === 8) {
    setSelectedProperties(selectedData);
    return setDownloadTemplate(true);
  }

  if (propertyStatusOptions.some((option) => option.value === value)) {
    propertyIds.forEach((id) =>
      changePropertyStatus({
        agencyid: currentAgency.agencyid,
        propertyid: id,
        pstatus: value,
      })
    );

    toast.success("Property status updated");
    tableRef?.current?.resetRowSelection();
    await isRefetch();
  }
};

  return (
    <>
      <MaterialReactTable
        // Tell MRT we are doing server-side pagination
        manualPagination
        rowCount={paginationData?.TotalRecords ?? 0} // <-- shows correct total & page count
        state={{ isLoading: isFetching, pagination }}
        onPaginationChange={setPagination}
        columns={AGENCY_PROPERTY_COLUMNS}
        tableInstanceRef={tableRef}
        data={data ?? []}
        enableFixed
        enableStickyHeader={!isMobileScreen}
        muiTableContainerProps={{
          sx: { maxHeight: isMobileScreen ? "100%" : "calc(100vh - 320px)" },
        }}
        enableRowSelection
        enableDensityToggle={false}
        initialState={{
          density: "compact",
          pagination: { pageSize: DEFAULT_SIZE }, // visual default
        }}
        // Row-per-page options (front-end controls size)
        muiTablePaginationProps={{
          rowsPerPageOptions: [5, 10, 15, 20, 50, 100, 200, 500, 1000],
          showFirstButton: true,
          showLastButton: true,
        }}
        getRowId={(row) => row.propertyid}
   renderTopToolbarCustomActions={({ table }) => {
  const selectedRows = table.getSelectedRowModel().rows;

  const propertyIds = selectedRows.map((row) => row.id);

  const locations = selectedRows.map(
    (row) => row.original.coordinates
  );

  const selectedData = selectedRows.map(
    (row) => row.original
  );

  const newVideos = selectedRows.map(
    (row) => row.original.propertyvideos || []
  );

  return (
    <TextField
      select
      variant="outlined"
      size="small"
      sx={{ minWidth: "150px" }}
      label="Action"
      value=""
      onChange={(e) =>
        handleStatusChange(
          Number(e.target.value),
          propertyIds,
          locations,
          selectedData,
          newVideos
        )
      }
    >
      {propertyStatusOptions.map((status) => (
        <MenuItem key={status.value} value={status.value}>
          {status.label}
        </MenuItem>
      ))}
    </TextField>
  );
}}
      />

      {shareLocation && (
        <div className="fixed top-0 left-0 z-10 bg-customGray w-full h-[100vh] flex justify-center items-center">
          <ShareLocation
            setShareLocation={setShareLocation}
            location={location}
          />
        </div>
      )}
 {downloadTemplate && (
        <ImageTemplates
          propertyData={selectedProperties}
          onClose={() => setDownloadTemplate(false)}
        />
      )}

      {shareYoutubeVideo && (
        <div className="fixed top-0 left-0 z-10 bg-customGray w-full h-[100vh] flex justify-center items-center">
          <ShareYoutubeVideo
            setShareYoutubeVideo={setShareYoutubeVideo}
            video={video}
          />
        </div>
      )}
    </>
  );
}
